/**
 * Müzakere ekleme sihirbazının kuralları (mobil muzakereSteps.ts ve
 * MuzakereWizard.tsx ile aynı): paket ve sorular, bilgiler, tekrar, tarih ve
 * web'de son adımda özet. Sorulu müzakere tekrarlanmaz (sunucu da reddeder):
 * soru varsa Tekrar adımı yok. Tarihler "YYYY-MM-DD" yerel gün.
 */
import { localDateString, parseLocalDate } from "../../lib/dateUtils";
import { questionsIssue, cloneQuestions, toPayload } from "./questionRules";

// Sunucudaki Muzakere modelinin sınırları.
export const MUZAKERE_TITLE_MAX = 100;
export const MUZAKERE_DESCRIPTION_MAX = 500;

// Sunucudaki sınırlarla aynı (muzakere.service MAX_REPEAT).
export const REPEAT_LIMITS = {
  weekly: { min: 2, max: 52, initial: 4 },
  monthly: { min: 2, max: 12, initial: 3 },
};

export const EMPTY_FORM = {
  title: "",
  description: "",
  date: "",
  repeat: "none",
  count: 1,
  packageId: null,
  questions: [],
};

/** Adımlar: questionsOnly'de yalnız sorular; soru varsa Tekrar adımı yok. */
export function wizardSteps({ questionsOnly = false, hasQuestions = false } = {}) {
  if (questionsOnly) return ["questions"];
  return hasQuestions
    ? ["questions", "info", "when", "summary"]
    : ["questions", "info", "repeat", "when", "summary"];
}

/**
 * Sorular adımından çıkarken: soru varsa müzakere tek sefer olur; önce
 * tekrar seçilmişse seçilen tarih de sıfırlanır (tekrar türüne göre seçilmişti).
 */
export function singleIfQuestions(form) {
  if (form.questions.length === 0 || form.repeat === "none") return form;
  return { ...form, repeat: "none", count: 1, date: "" };
}

/** Takvimde var olan "YYYY-MM-DD" günü mü (2026-02-30 değil). */
export function isValidDate(value) {
  const d = parseLocalDate(value);
  return !!d && localDateString(d) === value;
}

/** Adım tamam mı: sorular geçerli (sorusuz da olur), başlık dolu, tarih seçili ve geçerli. */
export function canAdvance(step, form, today) {
  if (step === "questions") return questionsIssue(form.questions) === null;
  if (step === "info") return form.title.trim().length > 0;
  if (step === "repeat") return ["none", "weekly", "monthly"].includes(form.repeat);
  if (step === "when") return isValidDate(form.date) && form.date >= minMuzakereDate(today);
  return true;
}

/** Tür değişince tarih yeniden seçilir, sayı türün başlangıç değerine döner. */
export function setRepeat(form, repeat) {
  if (form.repeat === repeat) return form;
  return { ...form, repeat, date: "", count: repeat === "none" ? 1 : REPEAT_LIMITS[repeat].initial };
}

/** Tekrar sayısı türün sınırlarında kalır. */
export function clampCount(repeat, count) {
  const limit = REPEAT_LIMITS[repeat];
  if (!limit) return 1;
  const n = Math.round(Number(count));
  if (!Number.isFinite(n)) return limit.min;
  return Math.min(limit.max, Math.max(limit.min, n));
}

/** Paket seçimi: sorular paketinkiyle değişir, boş başlık ve açıklama paketten dolar. */
export function applyPackage(form, pkg) {
  return {
    ...form,
    packageId: pkg.id,
    questions: cloneQuestions(pkg.questions),
    title: form.title.trim() ? form.title : String(pkg.title || "").slice(0, MUZAKERE_TITLE_MAX),
    description: form.description.trim()
      ? form.description
      : String(pkg.description || "").slice(0, MUZAKERE_DESCRIPTION_MAX),
  };
}

/** Geçmiş haftalara müzakere eklemek anlamsız (işaretlenemez): bu haftanın Pazartesi'si. */
export function minMuzakereDate(today) {
  const d = parseLocalDate(today) || new Date();
  const dow = d.getDay(); // 0 = Pazar
  d.setDate(d.getDate() - (dow === 0 ? 6 : dow - 1));
  return localDateString(d);
}

/** Pazartesi 1 … Pazar 7. */
const isoWeekday = (d) => (d.getDay() === 0 ? 7 : d.getDay());

/** Bugünden itibaren (bugün dahil) ilk denk gelen haftanın günü (1=Pzt … 7=Paz). */
export function nextWeekday(iso, today) {
  const d = parseLocalDate(today);
  d.setDate(d.getDate() + ((iso - isoWeekday(d) + 7) % 7));
  return localDateString(d);
}

const daysIn = (year, month) => new Date(year, month + 1, 0).getDate();

/**
 * Bugünden itibaren (bugün dahil) ayın o gününe tam denk gelen ilk tarih.
 * 31 seçilip bu ay 30 çekiyorsa bir sonraki 31 çeken ay; seri o günden
 * başlayınca sunucu kısa aylarda ayın son gününe kırpar.
 */
export function nextMonthDay(day, today) {
  const t0 = parseLocalDate(today);
  for (let i = 0; i < 13; i += 1) {
    const y = t0.getFullYear();
    const m = t0.getMonth() + i;
    const first = new Date(y, m, 1);
    if (daysIn(first.getFullYear(), first.getMonth()) < day) continue;
    const candidate = new Date(first.getFullYear(), first.getMonth(), day);
    if (candidate >= t0) return localDateString(candidate);
  }
  return today;
}

/** Hafta günü (1=Pzt … 7=Paz) ya da ayın günü; tarih yoksa null. */
export const weekdayOf = (date) => {
  const d = parseLocalDate(date);
  return d ? isoWeekday(d) : null;
};
export const monthDayOf = (date) => {
  const d = parseLocalDate(date);
  return d ? d.getDate() : null;
};

/**
 * Serinin tarihleri (sunucudaki repeatDates ile aynı kural): haftalıkta 7
 * gün arayla; aylıkta ilk tarihin günü, kısa aylarda ayın son günü.
 */
export function repeatDates(date, repeat, count) {
  const start = parseLocalDate(date);
  if (!start) return [];
  if (repeat !== "weekly" && repeat !== "monthly") return [date];
  const out = [];
  for (let i = 0; i < count; i += 1) {
    if (repeat === "weekly") {
      const d = new Date(start);
      d.setDate(start.getDate() + 7 * i);
      out.push(localDateString(d));
    } else {
      const y = start.getFullYear();
      const m = start.getMonth() + i;
      const first = new Date(y, m, 1);
      const day = Math.min(start.getDate(), daysIn(first.getFullYear(), first.getMonth()));
      out.push(localDateString(new Date(first.getFullYear(), first.getMonth(), day)));
    }
  }
  return out;
}

/** Oluşturma isteğinin gövdesi (POST /api/muzakeres/create). */
export function buildCreateBody(groupId, form) {
  const repeat = form.questions.length ? "none" : form.repeat;
  const body = {
    groupId: String(groupId),
    title: form.title.trim(),
    description: form.description.trim(),
    date: form.date,
    repeat,
    count: repeat === "none" ? 1 : form.count,
  };
  if (form.questions.length) {
    body.questions = form.questions.map(toPayload);
    if (form.packageId) body.packageId = form.packageId;
  }
  return body;
}

/** Soru düzenleme isteğinin gövdesi (POST /api/muzakeres/set-assessment). */
export function buildSetAssessmentBody(muzakereId, form) {
  const body = { muzakereId, questions: form.questions.map(toPayload) };
  if (form.questions.length && form.packageId) body.packageId = form.packageId;
  return body;
}
